import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, skip } from 'rxjs';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { AnnotatedOrder, DealerRemoteMaintenanceOrder } from 'src/app/generated/CrossPoint.License.Server';
import { BasicXpacInfo } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { OrderDetailsDialogData, OrderDetailsComponent } from 'src/app/modules/licenses/pages/order-details/order-details.component';
import { getOrdersAction, getOrderAction } from 'src/app/store/actions/licenses.actions';
import { selectLicenseOrders, selectOrder } from 'src/app/store/selectors/licenses.selectors';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-orders',
  templateUrl: './xpac-orders.component.html',
  styleUrls: ['./xpac-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpacOrdersComponent implements OnInit {

  private static readonly PAGE_SIZE: number = 25;
  private dialogRef: any = null;

  public isLoading: boolean = true; 
  public orderedLicenses: DealerRemoteMaintenanceOrder[] = [];
  public searchTimeoutHandle: number | null = null;
  public isLoadingOrderDetails: boolean = false;
  public isLoadingMore: boolean = false;
  public hasMore: boolean = true;

  private loadBeforeUtc: string = null;

  @Input() public xpac: BasicXpacInfo = null;

  constructor(private store: Store<IRemoteXPACPortalState>, private cdr: ChangeDetectorRef, private datePipe: LocalizedDatePipe, private dialog: DialogAnimationService,) { }

  public ngOnInit(): void {
    this.initListeners();
    this.initData();
  }

  private initListeners(): void {
    this.store.select(selectLicenseOrders)
      .pipe(
        // Ignore whatever was in the store to prevent issues with pagination
        skip(1),
        filter<DealerRemoteMaintenanceOrder[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((orders: DealerRemoteMaintenanceOrder[]) => {
        // Make sure only order with the xpac serial are used        
        orders = orders.filter(o => o.items.some(item => item.serial === this.xpac.deviceId));

        // Ensure DealerRemoteMaintenanceOrder are sorted with the most recent one on top
        orders.sort((a: DealerRemoteMaintenanceOrder, b: DealerRemoteMaintenanceOrder) => -(a.createdUtc.localeCompare(b.createdUtc)));
        this.hasMore = orders.length > XpacOrdersComponent.PAGE_SIZE;
        if (this.loadBeforeUtc !== null) {
          // This is a next page, add to the list
          this.orderedLicenses.push(...orders.slice(0, XpacOrdersComponent.PAGE_SIZE));
        }
        else {
          // 'first time' load, replace the list
          this.orderedLicenses = orders.slice(0, XpacOrdersComponent.PAGE_SIZE);
        }
        this.loadBeforeUtc = this.orderedLicenses[this.orderedLicenses.length - 1]?.createdUtc ?? null;
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      });
  }

  private initData(): void {
    this.store.dispatch(getOrdersAction({
      data: {
        searchText: this.xpac.deviceId,
        // Request 1 more so we know if there is more data after this page
        maxItems: XpacOrdersComponent.PAGE_SIZE + 1,
      }
    }));
  }

  public getInfoString(order: DealerRemoteMaintenanceOrder): string {
    let info: string = "";

    if (order.createdUtc != "") {
      info += this.datePipe.transform(order.createdUtc, 'shortDate');
    }

    return info;
  }

  public onOrderClick(order: DealerRemoteMaintenanceOrder): void {
    if (!this.isLoadingOrderDetails) {
      this.isLoadingOrderDetails = true;
      this.cdr.markForCheck();

      // first get the order details
      this.store.dispatch(getOrderAction({ data: order.id }));

      this.store.select(selectOrder(order.id))
        .pipe(
          filter(hasValue),
          untilDestroyed(this)
        )
        .subscribe((orderLicenses: AnnotatedOrder) => {
          if (orderLicenses.id === order.id) {
            const orderDetailsDialogData: OrderDetailsDialogData = { dealerRemoteMaintenanceOrder: order, annotatedOrder: orderLicenses };
            if (this.dialogRef === null) {
              this.dialogRef = this.dialog.open(OrderDetailsComponent, {
                panelClass: 'my-full-screen-dialog',
                animation: { to: "left" },
                data: orderDetailsDialogData,
                disableClose: true
              });

              this.dialogRef.afterClosed().subscribe((result: boolean) => {
                this.isLoadingOrderDetails = false;
                this.dialogRef = null;
                this.cdr.markForCheck();
              });
            }
          }
        });
    }
  }

  public loadMore(): void {
    this.isLoadingMore = true;
    this.store.dispatch(getOrdersAction({
      data: {
        endUtc: this.loadBeforeUtc,
        searchText: this.xpac.deviceId,
        maxItems: XpacOrdersComponent.PAGE_SIZE + 1,
      }
    }));
    this.cdr.markForCheck();
  }
}